import type { FormProduct, FormVariant } from "@shared/types";
import { REQUIRED_GRIND } from "../constants";
import type { BagSizeGroup } from "../types";

const REJECTED_VARIANT_STATUS = "Rejected";

const isRejectedVariant = (variant: FormVariant) => variant.status === REJECTED_VARIANT_STATUS;

const getSharedValues = (variant?: FormVariant): Omit<BagSizeGroup, "bagSize" | "grindTypes"> => {
  if (!variant) {
    return {};
  }

  return {
    price: variant.price,
    wholesalePrice: variant.wholesalePrice,
    startDate: variant.startDate,
    endDate: variant.endDate,
    term: variant.term,
    weeklyInventory: variant.weeklyInventory,
    totalMaxShortTermInventory: variant.totalMaxShortTermInventory,
    ship_to: variant.ship_to,
  };
};

const getSelectedAndRemoteBagSizeGroup = (product: FormProduct) => {
  const selectedVariants = product.variants?.filter((variant) => variant.isProductSelectedForImport) ?? [];
  const importedRemoteVariants =
    product.variants?.filter((variant) => variant.isImportedRemote && !variant.isImportedLocalVariant && !isRejectedVariant(variant)) ?? [];

  const bagSizesFromSelectedAndRemote = [
    ...new Set(
      [...selectedVariants, ...importedRemoteVariants]
        .filter((variant) => variant.mapDetectedBagSizeTo ?? variant.bagSize)
        .map((variant) => variant.mapDetectedBagSizeTo ?? variant.bagSize)
    ),
  ];

  const selectedAndRemoteGroups = bagSizesFromSelectedAndRemote.map((bagSize) => {
    const defaultGrindVariant = selectedVariants.find((variant) => {
      const detectedBagSize = variant.mapDetectedBagSizeTo ?? variant.bagSize;
      return detectedBagSize === bagSize && variant.grind?.includes(REQUIRED_GRIND);
    });
    const defaultGrindSharedValues = getSharedValues(defaultGrindVariant);

    const grindTypes = [...selectedVariants, ...importedRemoteVariants]
      .filter((variant) => {
        const detectedBagSize = variant.mapDetectedBagSizeTo ?? variant.bagSize;
        return detectedBagSize === bagSize && variant.grind;
      })
      .filter((variant) => variant?.grind?.length && variant?.grind?.length > 0)
      .flatMap((variant) => {
        if (variant.hasDetectedGrindOverride) {
          return variant.grind;
        }
        const detectedGrinds = variant.detectedGrindInfo ?? [];
        const mappedTo = detectedGrinds
          .map((detectedGrind) => detectedGrind.mapDetectedGrindTo)
          .filter((x): x is string => typeof x === "string");
        if (mappedTo.length === 0) {
          return variant.grind;
        }
        const unMappedOriginals = detectedGrinds.filter((grind) => !grind.mapDetectedGrindTo).map((grind) => grind.grind);
        return [...mappedTo, ...unMappedOriginals];
      })
      .filter((grindType) => typeof grindType === "string")
      .map((grindType) => grindType);
    return {
      bagSize: bagSize,
      grindTypes: [...new Set(grindTypes)],
      ...defaultGrindSharedValues,
    };
  });

  return selectedAndRemoteGroups;
};

const mergeSharedValues = (existing: BagSizeGroup, local: BagSizeGroup): BagSizeGroup => ({
  ...existing,
  grindTypes: [...new Set([...existing.grindTypes, ...local.grindTypes])],
  ...(local.price !== undefined ? { price: local.price } : {}),
  ...(local.wholesalePrice !== undefined ? { wholesalePrice: local.wholesalePrice } : {}),
  ...(local.startDate !== undefined ? { startDate: local.startDate } : {}),
  ...(local.endDate !== undefined ? { endDate: local.endDate } : {}),
  ...(local.term !== undefined ? { term: local.term } : {}),
  ...(local.weeklyInventory !== undefined ? { weeklyInventory: local.weeklyInventory } : {}),
  ...(local.totalMaxShortTermInventory !== undefined ? { totalMaxShortTermInventory: local.totalMaxShortTermInventory } : {}),
  ...(local.ship_to !== undefined ? { ship_to: local.ship_to } : {}),
});

const mergeGroupWithLocalGrinds = (selectedAndRemoteBagSizeGroups: BagSizeGroup[], importedLocalVariants: FormVariant[]) => {
  const mergedBagSizeGroupMap = new Map<string, BagSizeGroup>();

  const importedLocalBagSizeGroups = [
    ...new Set(
      importedLocalVariants
        .map((variant) => variant.mapDetectedBagSizeTo ?? variant.bagSize)
        .filter((bagSize): bagSize is string => Boolean(bagSize))
    ),
  ].map((bagSize) => {
    const localVariantsForBagSize = importedLocalVariants.filter(
      (variant) => (variant.mapDetectedBagSizeTo ?? variant.bagSize) === bagSize
    );
    const defaultGrindVariant =
      localVariantsForBagSize.find((variant) => variant.grind?.includes(REQUIRED_GRIND)) ?? localVariantsForBagSize[0];

    return {
      bagSize,
      grindTypes: [...new Set(localVariantsForBagSize.flatMap((variant) => variant.grind ?? []))],
      ...getSharedValues(defaultGrindVariant),
    } satisfies BagSizeGroup;
  });

  for (const bagSizeGroup of selectedAndRemoteBagSizeGroups) {
    mergedBagSizeGroupMap.set(bagSizeGroup.bagSize, { ...bagSizeGroup });
  }

  for (const localBagSizeGroup of importedLocalBagSizeGroups) {
    const existingBagSizeGroup = mergedBagSizeGroupMap.get(localBagSizeGroup.bagSize);

    if (!existingBagSizeGroup) {
      mergedBagSizeGroupMap.set(localBagSizeGroup.bagSize, localBagSizeGroup);
      continue;
    }

    mergedBagSizeGroupMap.set(localBagSizeGroup.bagSize, mergeSharedValues(existingBagSizeGroup, localBagSizeGroup));
  }

  return Array.from(mergedBagSizeGroupMap.values());
};

export const getBagSizeGroups = (product: FormProduct) => {
  const selectedAndRemoteGroups = getSelectedAndRemoteBagSizeGroup(product);
  const importedLocalVariants = product.variants?.filter((variant) => variant.isImportedLocalVariant) ?? [];
  const allImportedGroups = mergeGroupWithLocalGrinds(selectedAndRemoteGroups, importedLocalVariants);
  return { selectedAndRemoteGroups, allImportedGroups };
};
